import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
  Textarea,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  checkType,
  CtyButton,
  CtyShowToast,
  CtySpinner,
  SimpleDeleteConfirmationModal,
} from '@circuitry-ai/doc-ui';
import {
  useDeleteBusinessToolMutation,
  useGetBusinessToolsQuery,
} from '@circuitry-ai/doc-data';
import type { VoiceAgentBuiltInToolsState, WarmTransferToolFormState } from './voiceAgentToolsUtils';
import { isWarmTransferConfigured } from './voiceAgentToolsUtils';
import CreateBusinessToolModal from './CreateBusinessToolModal';

type BuiltInToolCardProps = Readonly<{
  title: string;
  description: string;
  enabled: boolean;
  status: string;
  icon: string;
  onToggle: (enabled: boolean) => void;
  isDisabled?: boolean;
  action?: React.ReactNode;
  footerAction?: React.ReactNode;
  clipDescription?: boolean;
  enabledLabel: string;
  disabledLabel: string;
}>;

function BuiltInToolCard({
  title,
  description,
  enabled,
  status,
  icon,
  onToggle,
  isDisabled = false,
  action,
  footerAction,
  clipDescription = false,
  enabledLabel,
  disabledLabel,
}: BuiltInToolCardProps) {
  const descriptionNode = description ? (
    clipDescription ? (
      <Tooltip
        content={
          <div
            className="max-w-sm text-xs"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        }
        placement="top"
        showArrow
      >
        <p className="mt-1 text-xs leading-5 text-default-500 line-clamp-2 cursor-default">
          {description}
        </p>
      </Tooltip>
    ) : (
      <p className="mt-1 text-xs leading-5 text-default-500">{description}</p>
    )
  ) : null;

  return (
    <div className="rounded-xl border border-divider bg-content1 p-4 shadow-sm transition-all hover:border-primary">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3 min-w-0">
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-default-100 text-default-700">
            <Icon icon={icon} width={20} height={20} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-foreground">{title}</p>
              <Chip
                size="sm"
                variant="flat"
                color={enabled ? 'success' : 'default'}
                classNames={{ content: 'text-xs' }}
              >
                {enabled ? enabledLabel : disabledLabel}
              </Chip>
            </div>
            {descriptionNode}
          </div>
        </div>
        <Switch
          isSelected={enabled}
          onValueChange={onToggle}
          isDisabled={isDisabled}
          aria-label={`Toggle ${title}`}
        />
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-divider bg-default-50 px-3 py-2">
        <div className="text-xs text-default-600 flex items-center gap-2 min-w-0">
          <span
            className={`inline-block h-2 w-2 shrink-0 rounded-full ${
              enabled ? 'bg-success' : 'bg-default-400'
            }`}
          />
          <span className="truncate">{status}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {enabled && action}
          {footerAction}
        </div>
      </div>
    </div>
  );
}

export interface VoiceAgentBuiltInToolsSectionProps {
  toolsState: VoiceAgentBuiltInToolsState;
  isUpdating?: boolean;
  onHangupChange: (enabled: boolean) => void;
  onFormCaptureChange: (enabled: boolean) => void;
  onWarmTransferToggle: (enabled: boolean) => void;
  onWarmTransferConfigSave: (config: WarmTransferToolFormState) => void;
  onBusinessToolChange: (toolId: string, enabled: boolean) => void;
}

const VoiceAgentBuiltInToolsSection: React.FC<VoiceAgentBuiltInToolsSectionProps> = ({
  toolsState,
  isUpdating = false,
  onHangupChange,
  onFormCaptureChange,
  onWarmTransferToggle,
  onWarmTransferConfigSave,
  onBusinessToolChange,
}) => {
  const [warmTransferModalOpen, setWarmTransferModalOpen] = useState(false);
  const [createToolModalOpen, setCreateToolModalOpen] = useState(false);
  const [editingToolId, setEditingToolId] = useState<string | null>(null);
  const [toolPendingDelete, setToolPendingDelete] = useState<{ id: string; name: string } | null>(
    null
  );
  const [draftWarmTransfer, setDraftWarmTransfer] = useState<WarmTransferToolFormState>(
    toolsState.warmTransfer
  );

  useEffect(() => {
    setDraftWarmTransfer(toolsState.warmTransfer);
  }, [toolsState.warmTransfer]);

  const warmTransferConfigured = isWarmTransferConfigured(toolsState.warmTransfer);

  const warmTransferStatus = toolsState.warmTransfer.enabled
    ? warmTransferConfigured
      ? 'Transfer destination is configured.'
      : 'Enabled, but setup is incomplete.'
    : 'Warm transfer is disabled.';

  const enabledLabel = 'Enabled';
  const disabledLabel = 'Disabled';

  const handleOpenWarmTransferModal = () => {
    setDraftWarmTransfer(toolsState.warmTransfer);
    setWarmTransferModalOpen(true);
  };

  const handleSaveWarmTransferConfig = () => {
    onWarmTransferConfigSave({
      ...draftWarmTransfer,
      enabled: true,
    });
    setWarmTransferModalOpen(false);
  };

  const { data: businessTools, isLoading: isBusinessToolsLoading } = useGetBusinessToolsQuery();
  const [deleteBusinessTool, { isLoading: isDeletingTool }] = useDeleteBusinessToolMutation();

  const handleConfirmDeleteTool = async () => {
    if (!toolPendingDelete) return;
    try {
      await deleteBusinessTool(toolPendingDelete.id).unwrap();
      if (toolsState.businessTools[toolPendingDelete.id]) {
        onBusinessToolChange(toolPendingDelete.id, false);
      }
      CtyShowToast(checkType.success, 'Business tool deleted');
      setToolPendingDelete(null);
    } catch (error: unknown) {
      const err = error as { data?: { message?: string }; message?: string };
      const message = err?.data?.message ?? err?.message ?? 'Failed to delete business tool';
      CtyShowToast(checkType.error, message);
    }
  };

  return (
    <>
      <Card className="bg-default-50 border border-divider">
        <CardBody className="gap-4">
          <div>
            <h3 className="text-base font-semibold text-foreground">Built-in tools</h3>
            <p className="text-sm text-default-500 mt-0.5">
              Control built-in capabilities for this voice agent. Configuration opens in a modal to
              keep the layout compact.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <BuiltInToolCard
              title="Hangup tool"
              description="Allow the assistant to end the call automatically when the conversation is complete."
              enabled={toolsState.hangupEnabled}
              onToggle={onHangupChange}
              isDisabled={isUpdating}
              status={
                toolsState.hangupEnabled
                  ? 'Assistant can end calls.'
                  : 'Assistant cannot end calls yet.'
              }
              icon="mdi:phone-hangup"
              enabledLabel={enabledLabel}
              disabledLabel={disabledLabel}
            />

            <BuiltInToolCard
              title="Warm transfer"
              description="Transfer the call to a live person when required."
              enabled={toolsState.warmTransfer.enabled}
              onToggle={onWarmTransferToggle}
              isDisabled={isUpdating}
              status={warmTransferStatus}
              icon="mdi:phone-forward"
              enabledLabel={enabledLabel}
              disabledLabel={disabledLabel}
              action={
                <CtyButton
                  size="sm"
                  variant="light"
                  className="h-8 min-w-0 px-2 text-xs"
                  onPress={handleOpenWarmTransferModal}
                  isDisabled={isUpdating}
                >
                  <Icon icon="mdi:cog-outline" width={14} height={14} className="mr-1" />
                  Configure
                </CtyButton>
              }
            />

            <BuiltInToolCard
              title="Form filling"
              description="Capture form fields from the conversation and send them to connected workflows."
              enabled={toolsState.formCaptureEnabled}
              onToggle={onFormCaptureChange}
              isDisabled={isUpdating}
              status={
                toolsState.formCaptureEnabled
                  ? 'Form capture is enabled.'
                  : 'Form capture is disabled.'
              }
              icon="mdi:form-textbox"
              enabledLabel={enabledLabel}
              disabledLabel={disabledLabel}
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-foreground">Business tools</h3>
            <CtyButton
              size="sm"
              color="primary"
              variant="flat"
              startContent={<Icon icon="mdi:plus" width={16} height={16} />}
              onPress={() => {
                setEditingToolId(null);
                setCreateToolModalOpen(true);
              }}
              isDisabled={isUpdating}
            >
              Add Tool
            </CtyButton>
          </div>

          {isBusinessToolsLoading ? (
            <div className="flex items-center justify-center">
              <CtySpinner size="sm" />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {businessTools?.data?.map((tool) => {
                const enabled = toolsState.businessTools[tool._id] === true;
                return (
                  <BuiltInToolCard
                    key={tool._id}
                    title={tool.name}
                    description={tool.description}
                    enabled={enabled}
                    onToggle={(nextEnabled) => onBusinessToolChange(tool._id, nextEnabled)}
                    isDisabled={isUpdating || isDeletingTool}
                    status={
                      enabled
                        ? `${tool.name} is attached to this voice agent.`
                        : `${tool.name} is not attached to this voice agent.`
                    }
                    icon="mdi:gear"
                    clipDescription
                    enabledLabel={enabledLabel}
                    disabledLabel={disabledLabel}
                    footerAction={
                      <div className="flex items-center gap-0.5">
                        <CtyButton
                          size="sm"
                          variant="light"
                          className="h-8 min-w-0 px-2 text-xs"
                          onPress={() => {
                            setEditingToolId(tool._id);
                            setCreateToolModalOpen(true);
                          }}
                          isDisabled={isUpdating || isDeletingTool}
                          aria-label={`Edit ${tool.name}`}
                        >
                          <Icon icon="mdi:pencil-outline" width={14} height={14} className="mr-1" />
                          Edit
                        </CtyButton>
                        <CtyButton
                          size="sm"
                          variant="light"
                          color="danger"
                          className="h-8 min-w-0 px-2 text-xs"
                          onPress={() => setToolPendingDelete({ id: tool._id, name: tool.name })}
                          isDisabled={isUpdating || isDeletingTool}
                          aria-label={`Delete ${tool.name}`}
                        >
                          <Icon icon="mdi:trash-can-outline" width={14} height={14} className="mr-1" />
                          Delete
                        </CtyButton>
                      </div>
                    }
                  />
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={warmTransferModalOpen}
        onOpenChange={setWarmTransferModalOpen}
        placement="center"
        backdrop="blur"
        size="2xl"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Warm transfer configuration</ModalHeader>
              <ModalBody className="gap-4">
                <p className="text-sm text-default-500">
                  Set the destination and prompt used when the assistant transfers a caller.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="Transfer phone number"
                    placeholder="+1 555 123 4567"
                    value={draftWarmTransfer.phone_number}
                    onValueChange={(value) =>
                      setDraftWarmTransfer((prev) => ({ ...prev, phone_number: value }))
                    }
                    variant="bordered"
                  />
                  <Input
                    label="Max transfer attempts"
                    type="number"
                    min={1}
                    step={1}
                    placeholder="3"
                    value={draftWarmTransfer.max_transfer_attempts}
                    onValueChange={(value) =>
                      setDraftWarmTransfer((prev) => ({ ...prev, max_transfer_attempts: value }))
                    }
                    variant="bordered"
                  />
                </div>
                <Textarea
                  label="Transfer prompt"
                  placeholder="Please hold while I transfer you to a specialist."
                  value={draftWarmTransfer.transfer_prompt}
                  onValueChange={(value) =>
                    setDraftWarmTransfer((prev) => ({ ...prev, transfer_prompt: value }))
                  }
                  variant="bordered"
                  minRows={3}
                />
              </ModalBody>
              <ModalFooter>
                <CtyButton variant="light" onPress={onClose}>
                  Cancel
                </CtyButton>
                <CtyButton
                  color="primary"
                  onPress={handleSaveWarmTransferConfig}
                  isLoading={isUpdating}
                  isDisabled={!isWarmTransferConfigured(draftWarmTransfer)}
                >
                  Apply
                </CtyButton>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <CreateBusinessToolModal
        isOpen={createToolModalOpen}
        toolId={editingToolId}
        onOpenChange={(open) => {
          setCreateToolModalOpen(open);
          if (!open) setEditingToolId(null);
        }}
      />

      <SimpleDeleteConfirmationModal
        isOpen={Boolean(toolPendingDelete)}
        onOpenChange={(open) => {
          if (!open) setToolPendingDelete(null);
        }}
        entityName={toolPendingDelete?.name}
        onConfirm={handleConfirmDeleteTool}
        isConfirmLoading={isDeletingTool}
      />
    </>
  );
};

export default VoiceAgentBuiltInToolsSection;
